import { formatTimeAgo, useNavigatorLanguage, type UseTimeAgoFormatter } from '@vueuse/core';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { gambits } from '../gambits';

function getNavigatorLocale(): string {
  const { language } = useNavigatorLanguage();
  return language.value || 'default';
}

export function getDayOfDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleDateString(getNavigatorLocale(), { day: '2-digit' });
}

export function getShortMonthOfDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleDateString(getNavigatorLocale(), { month: 'short' }).toUpperCase();
}

export function getTimeOfDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString(getNavigatorLocale(), { hour: '2-digit', minute: '2-digit' });
}

export function getShortDayOfWeek(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleDateString(getNavigatorLocale(), { weekday: 'short' }).toUpperCase();
}

export function getCompactDateTime(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return format(date, 'MMM/dd HH:mm');
}

export function getCompactDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return format(date, 'MMM/dd HH:mm');
}

export function getShortDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleDateString(getNavigatorLocale(), {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

export function getLongDate(dateTime?: string): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleDateString(getNavigatorLocale(), {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
}

export function validateBirthDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day) {
    return new Date().getFullYear() - year >= 14;
  } else {
    return false;
  }
}

function getTimeOfDayPhrase(hour: number): string {
  if (hour >= 5 && hour < 7) return 'at dawn';
  if (hour >= 7 && hour < 11) return 'in the morning';
  if (hour >= 11 && hour < 13) return 'at midday';
  if (hour >= 13 && hour < 17) return 'in the afternoon';
  if (hour >= 17 && hour < 19) return 'at dusk';
  if (hour >= 19 && hour < 22) return 'in the evening';
  return 'at night';
}

export function getPoeticTimestamp(dateTime: string): string {
  const date = new Date(dateTime);
  const dayName = date.toLocaleDateString('en', { weekday: 'long' });
  const timePhrase = getTimeOfDayPhrase(date.getHours());
  return `${dayName} ${timePhrase}`;
}

export function getMoveAgeOpacity(dateTime: string): number {
  const ms = Date.now() - new Date(dateTime).getTime();
  const days = ms / 86_400_000;
  if (days < 1) return 1;
  if (days < 7) return 0.8;
  if (days < 30) return 0.6;
  if (days < 90) return 0.4;
  return 0.25;
}

/**
 * Coarse time left until a future timestamp, localized and without a leading
 * preposition so it can sit inside a sentence. Rounds up, so a player is
 * never told to come back before the wait is actually over.
 */
export function getTimeRemaining(dateTime: string): string {
  const ms = new Date(dateTime).getTime() - Date.now();
  if (ms <= 0) return '';

  const minutes = Math.ceil(ms / 60_000);
  const [value, unit]: [number, 'minute' | 'hour'] =
    minutes < 60 ? [minutes, 'minute'] : [Math.ceil(minutes / 60), 'hour'];

  const locale = gambits.configService.getLanguage();
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' }).format(value);
}

/**
 * Month of a photo or a handshake. This year is the month alone. Any other
 * year keeps the month and adds the year. A handshake passes its own zone so
 * the photo and the list name the same month.
 */
export function formatMonthLabel(dateTime: string, timeZone?: string): string {
  if (!dateTime) return '';

  const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const year = formatInTimeZone(dateTime, zone, 'yyyy');
  const nowYear = formatInTimeZone(new Date(), zone, 'yyyy');

  return formatInTimeZone(dateTime, zone, year === nowYear ? 'LLL' : 'LLL yyyy');
}

export function getLocaleTimeAgo(date: Date): string {
  const locale = gambits.configService.getLanguage();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  return formatTimeAgo(date, {
    rounding: 'round',
    messages: {
      justNow: gambits.localizationService.localize('app.time.justNow'),
      past: (n: (string | UseTimeAgoFormatter<string>) & (string | UseTimeAgoFormatter<number>)) => n,
      future: (n: (string | UseTimeAgoFormatter<string>) & (string | UseTimeAgoFormatter<number>)) => n,
      ...Object.fromEntries(
        ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'].map((t: any) => [
          t,
          (n: number, past: number) => rtf.format(past ? 0 - n : n, t),
        ]),
      ),
      invalid: '',
    },
  });
}
